import React, { useState, useRef, useEffect } from 'react';
import { DayPicker } from 'react-day-picker';
import { parse, isValid, startOfDay, endOfDay, subDays, startOfMonth, endOfMonth, subMonths, subYears } from 'date-fns';
import 'react-day-picker/style.css';

// Use native JavaScript for formatting since date-fns format seems broken
const formatDate = (date, formatStr) => {
  if (!(date instanceof Date) || isNaN(date)) return '';

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  if (formatStr === 'yyyy-MM-dd') {
    return `${year}-${month}-${day}`;
  } else if (formatStr === 'MM/dd/yyyy') {
    return `${month}/${day}/${year}`;
  }
  return '';
};

/**
 * DateRangePicker Component
 *
 * A date range picker with manual input, calendar selection, and quick range shortcuts
 */
const DateRangePicker = ({
  fromDate,
  toDate,
  onFromDateChange,
  onToDateChange,
  disabled = false,
  placeholder = 'Select date range',
  width = '280px',
  style = {}
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [range, setRange] = useState({ from: null, to: null });
  const [fromInput, setFromInput] = useState('');
  const [toInput, setToInput] = useState('');
  const dropdownRef = useRef(null);

  // Initialize from props
  useEffect(() => {
    if (fromDate) {
      const date = parse(fromDate, 'yyyy-MM-dd', new Date());
      if (isValid(date)) {
        const formatted = formatDate(date, 'MM/dd/yyyy');
        setRange(prev => ({ ...prev, from: date }));
        setFromInput(formatted);
      }
    } else {
      setRange(prev => ({ ...prev, from: null }));
      setFromInput('');
    }
  }, [fromDate]);

  useEffect(() => {
    if (toDate) {
      const date = parse(toDate, 'yyyy-MM-dd', new Date());
      if (isValid(date)) {
        const formatted = formatDate(date, 'MM/dd/yyyy');
        setRange(prev => ({ ...prev, to: date }));
        setToInput(formatted);
      }
    } else {
      setRange(prev => ({ ...prev, to: null }));
      setToInput('');
    }
  }, [toDate]);

  // Quick range presets
  const quickRanges = [
    {
      label: 'Today',
      getValue: () => ({ from: startOfDay(new Date()), to: endOfDay(new Date()) })
    },
    {
      label: 'Yesterday',
      getValue: () => ({ from: startOfDay(subDays(new Date(), 1)), to: endOfDay(subDays(new Date(), 1)) })
    },
    {
      label: 'Last 7 Days',
      getValue: () => ({ from: startOfDay(subDays(new Date(), 6)), to: endOfDay(new Date()) })
    },
    {
      label: 'This Month',
      getValue: () => ({ from: startOfMonth(new Date()), to: endOfDay(new Date()) })
    },
    {
      label: 'Last Month',
      getValue: () => ({ from: startOfMonth(subMonths(new Date(), 1)), to: endOfMonth(subMonths(new Date(), 1)) })
    },
    {
      label: 'Last 3 Months',
      getValue: () => ({ from: startOfDay(subMonths(new Date(), 3)), to: endOfDay(new Date()) })
    },
    {
      label: 'Last 6 Months',
      getValue: () => ({ from: startOfDay(subMonths(new Date(), 6)), to: endOfDay(new Date()) })
    },
    {
      label: 'Last Year',
      getValue: () => ({ from: startOfDay(subYears(new Date(), 1)), to: endOfDay(new Date()) })
    },
  ];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const applyRange = (newRange) => {
    setRange(newRange);

    if (newRange.from) {
      const fromStr = formatDate(newRange.from, 'yyyy-MM-dd');
      const fromDisplayStr = formatDate(newRange.from, 'MM/dd/yyyy');
      setFromInput(fromDisplayStr);
      onFromDateChange(fromStr);
    }

    if (newRange.to) {
      const toStr = formatDate(newRange.to, 'yyyy-MM-dd');
      const toDisplayStr = formatDate(newRange.to, 'MM/dd/yyyy');
      setToInput(toDisplayStr);
      onToDateChange(toStr);
    }
  };

  const handleQuickRange = (quickRange) => {
    const newRange = quickRange.getValue();
    applyRange(newRange);
  };

  const handleRangeSelect = (selectedRange) => {
    if (selectedRange?.from || selectedRange?.to) {
      applyRange({
        from: selectedRange.from || range.from,
        to: selectedRange.to || range.to
      });
    }
  };

  const handleFromInputChange = (e) => {
    const value = e.target.value;
    setFromInput(value);
  };

  const handleToInputChange = (e) => {
    const value = e.target.value;
    setToInput(value);
  };

  const handleFromInputBlur = () => {
    if (!fromInput.trim()) {
      setRange(prev => ({ ...prev, from: null }));
      onFromDateChange('');
      return;
    }

    const parsed = parse(fromInput, 'MM/dd/yyyy', new Date());
    if (isValid(parsed)) {
      const dateStr = formatDate(parsed, 'yyyy-MM-dd');
      setRange(prev => ({ ...prev, from: parsed }));
      setFromInput(formatDate(parsed, 'MM/dd/yyyy'));
      onFromDateChange(dateStr);
    } else {
      // Invalid date, revert to previous value
      if (range.from) {
        setFromInput(formatDate(range.from, 'MM/dd/yyyy'));
      } else {
        setFromInput('');
      }
    }
  };

  const handleToInputBlur = () => {
    if (!toInput.trim()) {
      setRange(prev => ({ ...prev, to: null }));
      onToDateChange('');
      return;
    }

    const parsed = parse(toInput, 'MM/dd/yyyy', new Date());
    if (isValid(parsed)) {
      const dateStr = formatDate(parsed, 'yyyy-MM-dd');
      setRange(prev => ({ ...prev, to: parsed }));
      setToInput(formatDate(parsed, 'MM/dd/yyyy'));
      onToDateChange(dateStr);
    } else {
      // Invalid date, revert to previous value
      if (range.to) {
        setToInput(formatDate(range.to, 'MM/dd/yyyy'));
      } else {
        setToInput('');
      }
    }
  };

  const handleClear = () => {
    setRange({ from: null, to: null });
    setFromInput('');
    setToInput('');
    onFromDateChange('');
    onToDateChange('');
  };

  const getDisplayText = () => {
    if (fromInput && toInput) {
      return `${fromInput} - ${toInput}`;
    } else if (fromInput) {
      return `From ${fromInput}`;
    } else if (toInput) {
      return `Until ${toInput}`;
    }
    return placeholder;
  };

  return (
    <div className="date-range-picker" style={{ position: 'relative', width, ...style }} ref={dropdownRef}>
      {/* Trigger Button */}
      <div className="input-group input-group-sm">
        <input
          type="text"
          className="form-control form-control-sm"
          value={getDisplayText()}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          readOnly
          disabled={disabled}
          style={{ cursor: disabled ? 'not-allowed' : 'pointer', backgroundColor: 'white' }}
        />
        <button
          className="btn btn-outline-secondary"
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
        >
          <i className="fas fa-calendar"></i>
        </button>
        {(fromInput || toInput) && !disabled && (
          <button
            className="btn btn-outline-secondary"
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleClear();
            }}
            title="Clear dates"
          >
            <i className="fas fa-times"></i>
          </button>
        )}
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div
          className="dropdown-menu show p-3"
          style={{ position: 'absolute', top: '100%', left: 0, zIndex: 1050, marginTop: '4px', width: 'max-content' }}
        >
          <div className="d-flex gap-3">
            {/* Quick Ranges */}
            <div style={{ minWidth: '130px' }}>
              <div className="mb-2 fw-bold text-muted" style={{ fontSize: '0.8rem' }}>Quick Ranges</div>
              <div className="d-flex flex-column gap-1">
                {quickRanges.map((qr, idx) => (
                  <button
                    key={idx}
                    type="button"
                    className="btn btn-sm btn-outline-primary text-start"
                    onClick={() => handleQuickRange(qr)}
                    style={{ fontSize: '0.8rem', padding: '0.25rem 0.5rem' }}
                  >
                    {qr.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Calendar & Manual Input */}
            <div style={{ flex: 1 }}>
              {/* Manual Input Fields */}
              <div className="mb-2">
                <div className="row g-2">
                  <div className="col">
                    <label className="form-label" style={{ fontSize: '0.8rem', marginBottom: '2px' }}>From Date</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={fromInput}
                      onChange={handleFromInputChange}
                      onBlur={handleFromInputBlur}
                      placeholder="MM/DD/YYYY"
                      style={{ fontSize: '0.8rem', padding: '0.25rem 0.5rem' }}
                    />
                  </div>
                  <div className="col">
                    <label className="form-label" style={{ fontSize: '0.8rem', marginBottom: '2px' }}>To Date</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={toInput}
                      onChange={handleToInputChange}
                      onBlur={handleToInputBlur}
                      placeholder="MM/DD/YYYY"
                      style={{ fontSize: '0.8rem', padding: '0.25rem 0.5rem' }}
                    />
                  </div>
                </div>
              </div>

              {/* Calendar */}
              <DayPicker
                mode="range"
                selected={range}
                onSelect={handleRangeSelect}
                numberOfMonths={2}
                animate
              />

              {/* Close Button */}
              <div className="mt-2 text-end">
                <button
                  type="button"
                  className="btn btn-sm btn-secondary"
                  onClick={() => setIsOpen(false)}
                  style={{ fontSize: '0.8rem', padding: '0.25rem 0.75rem' }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangePicker;
